import { getRouter } from '@/router';
import { defineStore } from 'pinia';
import { jwtDecode, InvalidTokenError } from 'jwt-decode';
import { JwtClaims, type UserContext } from '@/api';
import { CompanyUpdated } from '@/api/events';
import { ZodError } from 'zod';
import { cloneDeep } from 'lodash';
import * as Sentry from '@sentry/vue';

export const useClientStore = defineStore(
    'client',
    () => {
        // #region JWT
        const lastRoute = ref<string | undefined>();
        const auth = reactive({
            /**
             * The current JWT token for this user.
             */
            jwt: undefined as string | undefined,
            // me: undefined as User | undefined,
            // company: undefined as Company | undefined,
            // paymentPlans: [] as PaymentPlan[],
            passwordResetToken: undefined as string | undefined,
            referral: undefined as
                | {
                      id: number;
                      name: string;
                  }
                | undefined,
            /**
             * Was this session validated when the browser loaded this page?
             *
             * On page load, we want to validate any existing session token we might have stored.
             * This variable helps us keep track if we have already done that.
             */
            onloadSessionValidated: false,
            /**
             * Is this session valid?
             */
            sessionValid: false,
            error: undefined as any | undefined,
            selectedStudentId: undefined as number | undefined,
        });

        const context = ref<UserContext>();

        const selectedStudentId = computed<number | undefined>({
            get: () => {
                const set = context.value?.students.find(s => s.id === auth.selectedStudentId);
                if (set) {
                    return set.id;
                }
                return context.value?.students[0]?.id;
            },
            set: v => {
                if (v == null || context.value?.students.find(s => s.id === v)) {
                    auth.selectedStudentId = v;
                }
            },
        });

        /**
         * Must the current auth data be validated?
         */
        const mustBeValidated = computed(() => auth.sessionValid !== true && auth.jwt != null);

        /**
         * Parsed JWT claims.
         *
         * @returns `undefined`: If the JWT token is not defined or invalid. `JwtClaims`: Otherwise.
         */
        const jwtClaims = computed(() => {
            if (auth.jwt == null) {
                return undefined;
            }
            try {
                const jwtPayload = jwtDecode(auth.jwt);
                const claims = JwtClaims.parse(jwtPayload);
                if (claims == null) {
                    console.error('Invalid JWT claims!', claims);
                }
                return claims ?? undefined;
            } catch (e) {
                if (e instanceof InvalidTokenError) {
                    console.error('Invalid JWT token!', e);
                } else if (e instanceof ZodError) {
                    console.error('Invalid JWT claims!', e);
                }
                return undefined;
            }
        });

        /**
         * Update the `jwt_claims` context for the sentry reporter.
         */
        watch(jwtClaims, claims => Sentry.setContext('jwt_claims', claims ?? null), {
            immediate: true,
            deep: true,
        });

        /**
         * Checks if the current JWT is expired.
         *
         * @param thresholdSeconds If the JWT expires within this many seconds in the future, we'll consider it expired. Defaults to 0.
         */
        const isJwtExpired = (thresholdSeconds = 0) => {
            if (jwtClaims.value == null) {
                return true;
            }
            const nowUtcSeconds = Math.floor(new Date().getTime() / 1000); //time now in seconds
            if (nowUtcSeconds + thresholdSeconds >= jwtClaims.value.exp) {
                return true;
            }
            return false;
        };

        const { isLoading: isRefreshingJwtToken, withLoading: withRefreshingJwtToken } = useLoading();
        const refreshJwtToken = withRefreshingJwtToken(async (force = false) => {
            console.log('Refreshing JWT token...');
            try {
                const result = await api.refreshJwt(undefined);
                auth.jwt = result.token;
                auth.sessionValid = false;
                await validateSessionToken();
            } catch (e) {
                console.error(e);
            }
        });

        // #endregion

        // #region Session

        const handleLogin = async (result: ZodiosResponseByAlias<typeof api.api, 'login'>) => {
            auth.jwt = result.token;
            auth.sessionValid = false;
            return await validateSessionToken();
        };

        const logout = async () => {
            Sentry.addBreadcrumb({ message: 'Logout' });
            Sentry.setUser(null);
            auth.error = undefined;
            context.value = undefined;
            auth.jwt = undefined;
            auth.sessionValid = false;
            return await validateSessionToken();
        };

        const { withLoading: withValidatingSession, isLoading: isValidatingSession } = useLoading({
            name: 'client store',
            provide: 'none',
            inject: false,
        });

        const refreshContext = async () => {
            try {
                const { data } = await api.getUserContext();
                context.value = data;
            } catch (e) {
                console.error(e);
            }
        };

        const validateSessionToken = withValidatingSession(async (): Promise<boolean> => {
            try {
                console.log('Verifying session token...');
                auth.error = undefined;
                if (auth.jwt == null) {
                    auth.sessionValid = false;
                    Sentry.setUser(null);
                    await checkRouteAccess();
                    return false;
                }
                if (!auth.sessionValid || context.value == null) {
                    try {
                        const { data } = await api.getUserContext();
                        context.value = data;
                        auth.sessionValid = true;
                        auth.passwordResetToken = undefined;
                        Sentry.setUser({
                            id: data.user.id,
                            email: data.user.email,
                            username: `${data.user.account_type}#${data.user.company_id}: ${data.user.given_name} ${data.user.family_name}`,
                        });
                        await checkRouteAccess();
                        return true;
                    } catch (error) {
                        auth.error = undefined;
                        context.value = undefined;
                        auth.jwt = undefined;
                        auth.sessionValid = false;
                        // auth.paymentPlans = [];
                        if (isErrorFromAlias(api.api, 'getAccount', error)) {
                            if (error.response.status === 403) {
                                auth.error = error.response.data.message;
                            } else {
                                auth.error = 'An unknown error occurred.';
                            }
                        }
                        console.error(error);
                        await checkRouteAccess();
                        return false;
                    }
                }
                return true;
            } finally {
                auth.onloadSessionValidated = true;
            }
        });

        const visitingApplicationId = ref<number | undefined>();
        const checkRouteAccess = async () => {
            try {
                const router = getRouter();
                let tries = 25;
                while (tries > 0) {
                    tries--;
                    if (!router.currentRoute.value?.name || router.currentRoute.value?.name === 'manage-application') {
                        console.log('Route name is null, retrying in 200ms...');
                        await new Promise(resolve => setTimeout(resolve, 200));
                        if (tries === 0) {
                            // get application id from current url /u/application/{id}
                            visitingApplicationId.value = Number(window.location.pathname.split('/u/applications/')[1]);
                            if (Number.isNaN(visitingApplicationId.value)) {
                                visitingApplicationId.value = undefined;
                            }
                        }
                    }
                }

                const name = router.currentRoute.value?.name;
                const unauth = router.currentRoute.value?.meta.layout === 'unauth';
                if (name === 'notfound') {
                    console.log('Not found route.');
                    return;
                }
                if (mustBeValidated.value && (auth.onloadSessionValidated === false || !unauth)) {
                    console.log('Route access check will validate session token.');
                    validateSessionToken();
                } else if (auth.passwordResetToken != null) {
                    console.log('To password reset!');
                    await router.push({ name: 'reset-password' });
                } else if (auth.referral != null) {
                    console.log('To student register!');
                    await router.push({ name: 'student-register' });
                } else if (auth.sessionValid === false && !unauth) {
                    console.log('To login!');
                    await router.push({ name: 'login' });
                } else if ((auth.sessionValid && (unauth || name === undefined)) || name === 'onboarding') {
                    if (name === undefined) {
                        console.log('Route is not loaded. Resetting to most suitable starting point...');
                    }
                    if (jwtClaims.value?.scopes.includes('onboarded') || context.value?.user.finished_onboarding) {
                        console.log('To home page!');
                        // if (context.value?.user.account_type === 'student') {
                        //     console.log('To manage student!');
                        //     await router.push({
                        //         name: 'manage-student',
                        //         params: { studentId: selectedStudentId.value },
                        //     });
                        // } else {
                        console.log('To dashboard!');
                        await router.push({ name: 'dashboard' });
                        // }
                    } else {
                        console.log('To onboarding!');
                        await router.push({ name: 'onboarding' });
                    }
                } else if (
                    false &&
                    auth.sessionValid &&
                    name === 'dashboard' &&
                    context.value?.user.account_type === 'student'
                ) {
                    console.log('To manage student from dashboard!');
                    await router.push({ name: 'manage-student', params: { studentId: selectedStudentId.value } });
                } else {
                    console.log('Staying on same route!', name, auth, unauth);
                }
            } catch (e) {
                console.error('Error during loading route: ', e);
                const router = getRouter();
                if (auth.sessionValid) {
                    await router.push({ name: 'dashboard' });
                } else {
                    await router.push({ name: 'login' });
                }
            }
        };

        // #endregion

        // update company
        useChannel(
            () => (context.value?.company == null ? undefined : `companies.${context.value.company.id}`),
            CompanyUpdated,
            data => {
                if (data && context.value) {
                    context.value = {
                        ...context.value,
                        company: {
                            ...context.value.company,
                            ...data.data,
                        },
                    };
                }
            },
        );

        const isLoggedIn = computed(() => auth.sessionValid);

        return {
            auth,
            context,
            jwtClaims,
            isLoggedIn,
            isValidatingSession,
            handleLogin,
            refreshJwtToken,
            isJwtExpired,
            validateSessionToken,
            checkRouteAccess,
            logout,
            refreshContext,
            selectedStudentId,
            visitingApplicationId,
        };
    },
    {
        persist: {
            storage: localStorage,
            // Renamed from `paths` in pinia-plugin-persistedstate 4. The old key is silently
            // ignored rather than rejected, which would persist and rehydrate the whole store.
            pick: ['auth.jwt', 'context', 'auth.selectedStudentId'],
        },
    },
);
